import { yupResolver } from "@hookform/resolvers/yup";
import { DeleteOutlined, OpenInNewOutlined } from "@mui/icons-material";
import {
  Box,
  Card,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { Resolver, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { formatDate } from "../../../shared/functions/formatDate";
import { handleError } from "../../../shared/functions/handleError";
import { IClientAction } from "../../../shared/interfaces/client/IClient";
import { instance } from "../../../utils/api/api.service";
import { OFFER_URL } from "../../../utils/api/endpoints";
import { ClientService } from "../service";
import { clientSchema } from "./ClientCreate";
import ClientForm from "./ClientForm";

const ClientUpdate = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id } = useParams();
  const [offerFiles, setOfferFiles] = useState<{ filename: string; createdAt: string; size: number }[]>([]);

  const loadFiles = () => {
    if (id) {
      instance.get(`${OFFER_URL}/${id}/files`).then((r) => setOfferFiles(r.data)).catch(() => {});
    }
  };

  useEffect(() => {
    loadFiles();
  }, [id]);

  const handleDeleteFile = (filename: string) => {
    instance.delete(`${OFFER_URL}/${id}/files/${filename}`)
      .then(loadFiles)
      .catch(handleError);
  };

  const handleOpenFile = (filename: string) => {
    instance.get(`${OFFER_URL}/${id}/files/${filename}`, { responseType: "blob" })
      .then((r) => {
        const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 10000);
      })
      .catch(handleError);
  };
  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm<IClientAction>({
    resolver: yupResolver(clientSchema) as unknown as Resolver<IClientAction>,
    defaultValues: async () =>
      (await ClientService.getSingle(Number(id))
        .then((response) => {
          return {
            ...response,
            modules: response?.modules?.map((module) => ({
              moduleId: module?.id,
            })),
          };
        })
        .catch(handleError)) as unknown as IClientAction,
  });

  const submitHandler = (formData: IClientAction) => {
    ClientService.update(
      {
        ...formData,
        expirationDate: formatDate(formData?.expirationDate, "YYYY-MM-DD"),
      },
      Number(id),
    )
      .then((response) => {
        if (response) {
          navigate(-1);
          enqueueSnackbar(
            t("toasts.successfullyUpdated", { title: formData?.name }),
            {
              variant: "success",
            },
          );
        }
      })
      .catch(handleError);
  };

  return (
    <>
      <form onSubmit={handleSubmit(submitHandler)}>
        <ClientForm isUpdate control={control} watch={watch} errors={errors} />
      </form>

      {offerFiles.length > 0 && (
        <Card sx={{ mt: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
            {t("offer.generatedFiles")}
          </Typography>
          <Divider sx={{ mb: 1 }} />
          <List dense disablePadding>
            {offerFiles.map((f) => (
              <ListItem
                key={f.filename}
                disableGutters
                secondaryAction={
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <IconButton size="small" onClick={() => handleOpenFile(f.filename)}>
                      <OpenInNewOutlined fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDeleteFile(f.filename)}>
                      <DeleteOutlined fontSize="small" />
                    </IconButton>
                  </Box>
                }
              >
                <ListItemText
                  primary={f.filename}
                  secondary={`${new Date(f.createdAt).toLocaleString()} · ${(f.size / 1024).toFixed(0)} KB`}
                />
              </ListItem>
            ))}
          </List>
        </Card>
      )}
    </>
  );
};

export default ClientUpdate;
